import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../config';
import { 
    Autocomplete, 
    TextField, 
    InputAdornment, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Button,
    Tabs,
    Tab,
    Box,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddHomeIcon from '@mui/icons-material/AddHome';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory } from 'react-router-dom';
import HouseholdForm from '../../components/forms/HouseholdForm/HouseholdForm';
import HouseholdOverview from './HouseholdOverview';
import HouseholdMembers from './HouseholdMembers';
import HouseholdProjects from './HouseholdProjects';
import HouseholdTasks from './HouseholdTasks';
import HouseholdNotes from './HouseholdNotes';
import HouseholdFiles from './HouseholdFiles';

export default function Households(props) {
    const history = useHistory();
    // Determine customer label based on company settings
    const customerLabel = props?.user?.comp_settings?.filter(setting => [101, 102, 104].includes(setting.feature_id)).length > 0 ? 'Client'
        : props?.user?.comp_settings?.filter(setting => setting.feature_id === 105).length > 0 ? 'Member' : 'Customer';

    const [state, setState] = useState({
        households: [],
        active: [],
        inactive: [],
        activeList: 'all',
        searchTerm: '',
        householdSearchResults: [],
        isLoading: false,
        current_household: null,
        selectedTab: 0,
    });
    const [inputValue, setInputValue] = useState('');
    const [showHouseholdForm, setShowHouseholdForm] = useState(false);
    const [editingHouseholdId, setEditingHouseholdId] = useState(null);

    const fetchHouseholds = useCallback(async () => {
        if (!props.user?.comp_id) return;
        
        setState(prev => ({ ...prev, isLoading: true }));
        try {
            const response = await axios.get(`${APIURL}/households`, {
                params: { comp_id: props.user.comp_id }
            });
            const households = response.data || [];
            const active = households.filter(h => h.is_actively_serviced);
            const inactive = households.filter(h => !h.is_actively_serviced);

            setState(prev => ({
                ...prev,
                households: households,
                active: active,
                inactive: inactive,
                householdSearchResults: households,
                current_household: households.length > 0 ? households[0].household_id : null,
                isLoading: false
            }));
        } catch (error) {
            console.error('Error fetching households:', error);
            setState(prev => ({
                ...prev,
                households: [],
                active: [],
                inactive: [],
                householdSearchResults: [],
                current_household: null,
                isLoading: false
            }));
        }
    }, [props.user?.comp_id]);

    useEffect(() => {
        fetchHouseholds();
    }, [fetchHouseholds]);

    const handleHouseholdSelection = (event, value) => {
        if (value && typeof value === 'object' && value?.household_id) {
            setState(prev => ({
                ...prev,
                current_household: value.household_id,
                searchTerm: value.household_name
            }));
            setInputValue(value.household_name);
        }
    };

    const handleStatusChange = (event) => {
        const selectedType = event.target.value;
        let newHouseholdList;
        
        if (selectedType === 'all') {
            newHouseholdList = state.households;
        } else {
            newHouseholdList = state[selectedType];
        }
        
        setState(prev => ({
            ...prev,
            activeList: selectedType,
            householdSearchResults: newHouseholdList,
            current_household: newHouseholdList.length > 0 ? newHouseholdList[0].household_id : null,
            searchTerm: ''
        }));
        setInputValue('');
    };

    const showNewHousehold = () => {
        setEditingHouseholdId(null);
        setShowHouseholdForm(true);
    };

    // eslint-disable-next-line no-unused-vars
    const handleEditHousehold = (householdId) => {
        setEditingHouseholdId(householdId);
        setShowHouseholdForm(true);
    };

    const handleCloseHouseholdForm = () => {
        setShowHouseholdForm(false);
        setEditingHouseholdId(null);
    };

    const handleSaveHousehold = async () => {
        // Refresh household list from API after save
        await fetchHouseholds();
        handleCloseHouseholdForm();
    };

    const handleTabChange = (event, newValue) => {
        setState(prev => ({
            ...prev,
            selectedTab: newValue
        }));
    };

    const renderTabContent = () => {
        switch (state.selectedTab) {
            case 0:
                return (
                    <HouseholdOverview
                        household_id={state.current_household}
                        customerLabel={customerLabel}
                        onNavigateToTasks={() => setState(prev => ({ ...prev, selectedTab: 3 }))}
                    />
                );
            case 1:
                return (
                    <HouseholdMembers
                        household_id={state.current_household}
                        customerLabel={customerLabel}
                        onMemberClick={(member) => {
                            const isCustomer = member.member_type === 'customer' || member.customer_id != null;
                            history.push(isCustomer ? {
                                pathname: '/customers',
                                customersProps: { cus_id: member.customer_id, edit: false }
                            } : {
                                pathname: '/contacts',
                                contactsProps: { con_id: member.contact_id }
                            });
                        }}
                    />
                );
            case 2:
                return <HouseholdProjects {...props} history={history} household_id={state.current_household} user={props.user} />;
            case 3:
                return <HouseholdTasks {...props} history={history} household_id={state.current_household} user={props.user} />;
            case 4:
                return <HouseholdFiles household_id={state.current_household} user={props.user} customerLabel={customerLabel} />;
            case 5:
                return <HouseholdNotes {...props} household_id={state.current_household} user={props.user} customerLabel={customerLabel} />;
            default:
                return null;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 90px)', width: '100%', overflow: 'hidden' }}>
            {state.isLoading ? (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    width: '100%'
                }}>
                    <CircularProgress size={60} />
                </Box>
            ) : (
            <div className="householdsPage-mainDiv" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'flex-end',
                            paddingRight: '15px',
                            borderRight: '2px solid #257bcd',
                            flexDirection: 'column',
                            textAlign: 'right'
                        }}>
                            <span style={{ 
                                fontSize: '12px',
                                color: '#666',
                                fontWeight: '500',
                            }}>
                                Current Household
                            </span>
                            <span style={{ 
                                fontSize: '18px', 
                                fontWeight: '600',
                                color: state.current_household ? '#333' : '#999',
                                fontStyle: state.current_household ? 'normal' : 'italic'
                            }}>
                                {state.current_household 
                                    ? state.households.find(household => household.household_id === state.current_household)?.household_name || 'Unknown Household'
                                    : 'No household selected'
                                }
                            </span>
                        </div>
                        <Autocomplete
                            freeSolo
                            clearOnBlur={true}
                            inputValue={inputValue}
                            onInputChange={(event, newInput) => setInputValue(newInput)}
                            options={state.householdSearchResults}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.household_name}
                            onChange={handleHouseholdSelection}
                            disableClearable
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Households"
                                    variant="outlined"
                                    size="small"
                                    style={{ width: '300px' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, item) => (
                                <li {...props} key={item.household_id}>
                                    {item.household_name}
                                </li>
                            )}
                        />
                        <FormControl variant="outlined" size="small" style={{ width: '200px' }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={state.activeList}
                                onChange={handleStatusChange}
                                label="Status"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {state.current_household && (
                            <Button 
                                onClick={() => handleEditHousehold(state.current_household)} 
                                variant="contained" 
                                color="secondary"
                                startIcon={<EditIcon />}
                            >
                                Edit Household
                            </Button>
                        )}
                        {true && (
                            <Button 
                                onClick={showNewHousehold} 
                                variant="contained" 
                                color="primary"
                                startIcon={<AddHomeIcon />}
                            >
                                New Household
                            </Button>
                        )}
                    </div>
                </div>
                
                <div style={{ display: 'flex', height: 'calc(100vh - 200px)' }}>
                    <div style={{ width: '100%' }}>
                        {state.householdSearchResults.length === 0 && (
                            <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '500px', marginTop: 10 }}>
                                <img
                                    src='./nosearchresult.png'
                                    alt="no households"
                                    style={{ width: '15rem', height: '10rem', margin: 10 }}
                                />
                                <h5 style={{ fontFamily: 'Lato', marginTop: 48, marginBottom: 30 }} className="text-center">
                                    No Households Found
                                </h5>
                                <p style={{ fontFamily: 'Lato', fontSize: 12, width: '70%' }} className="text-muted text-center text-wrap">
                                    Household information including members, location, and service details will show here once you add households.
                                    <br />
                                    On the top, you can search for households or filter by status.
                                </p>
                            </div>
                        )}
                        {state.householdSearchResults.length > 0 && state.current_household && (
                            <div style={{ width: '100%', height: '100%', paddingLeft: '15px', display: 'flex', flexDirection: 'column' }}>
                                <Tabs
                                    value={state.selectedTab}
                                    onChange={handleTabChange}
                                    variant="fullWidth"
                                    scrollButtons="auto"
                                    style={{ flexShrink: 0, width: '100%' }}
                                >
                                    <Tab label="OVERVIEW" value={0} />
                                    <Tab label="MEMBERS" value={1} />
                                    <Tab label="PROJECTS" value={2} />
                                    <Tab label="TASKS" value={3} />
                                    <Tab label="FILES" value={4} />
                                    <Tab label="NOTES" value={5} />
                                </Tabs>
                                <div className='details_body' style={{ overflow: 'auto', flex: 1, minHeight: 0, paddingRight: '12px' }}>
                                    {renderTabContent()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            )}
            
            {showHouseholdForm && (
                <HouseholdForm
                    open={showHouseholdForm}
                    onClose={handleCloseHouseholdForm}
                    onSave={handleSaveHousehold}
                    edit={!!editingHouseholdId}
                    household_id={editingHouseholdId}
                    user={props.user}
                    customerLabel={customerLabel}
                />
            )}
        </div>
    );
}
